import * as Api from '@ViewModels';
import React from 'react';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { isSystemJobInFlight, useGetSystemJobQuery } from '../../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	type BrandingSystemJobResult,
	IMAGE_BRANDING_ASSISTANT_NAME,
	requestImageBrandingStream,
} from './imageBrandingStream';

type Params = {
	impersonationContext?: Api.IImpersonationContext;
};

export const useApplyBrandingToImages = ({ impersonationContext }: Params) => {
	const logger = useEventLogging('BulkRebrandingTab');

	const userSession = useUserSession();
	const [systemJobId, setSystemJobId] = React.useState<string | undefined>(undefined);
	// Number of images the current run was asked to brand. Gives a stable total to display
	// before the system job's first poll (which carries metadata.totalImages) arrives.
	const [requestedImageCount, setRequestedImageCount] = React.useState(0);

	const [conversationId, setConversationId] = React.useState<string | undefined>(undefined);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				setConversationId(data.id);
			}
		},
	});

	React.useEffect(() => {
		// Skip creating a new conversation if we already have one
		if (conversationId) {
			return;
		}

		if (aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName: IMAGE_BRANDING_ASSISTANT_NAME,
				impersonationContext,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const systemJobQuery = useGetSystemJobQuery<BrandingSystemJobResult>({
		enabled: Boolean(systemJobId),
		jobId: systemJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!systemJobId) {
				return false;
			}

			return !data || isSystemJobInFlight(data.status) ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ attachmentIds }: { attachmentIds: string[] }) => {
			if (!conversationId) {
				return Promise.reject(new Error('No image assistant conversation'));
			}
			return requestImageBrandingStream({
				userSession,
				conversationId,
				impersonationContext,
				attachmentIds,
			});
		},
		onEvent: event => {
			if (event.event !== 'ToolResponse') {
				return;
			}
			let data: { toolCall?: { systemJobId?: string } };
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			const toolCall = data?.toolCall;

			if (toolCall?.systemJobId) {
				setSystemJobId(toolCall.systemJobId);
			}
		},
	});

	const applyBranding = ({ attachmentIds }: { attachmentIds: string[] }) => {
		setRequestedImageCount(attachmentIds.length);
		aiPromptMutation.mutate({ attachmentIds });
	};

	const metadata = systemJobQuery.data?.metadata;
	const totalImages = metadata?.totalImages || requestedImageCount || 0;
	const completedImages = (metadata?.successfulImages ?? 0) + (metadata?.failedImages ?? 0);

	return {
		applyBranding,
		isGenerating:
			aiPromptMutation.isLoading || systemJobQuery.isLoading || isSystemJobInFlight(systemJobQuery.data?.status),
		resultImages: systemJobQuery.data?.resultImages,
		error: systemJobQuery.error,
		failedImages: systemJobQuery.data?.metadata?.failedImages || 0,
		totalImages,
		completedImages,
		currentPhase: metadata?.currentPhase,
		currentImageNumber: metadata?.currentImageNumber,
		currentImageAttempt: metadata?.currentImageAttempt,
		systemJobId,
		systemJobStatus: systemJobQuery.data?.status,
	};
};
